import axios from '../config/axiosConfig';

const API_URL = '/agents';

// Executor types
export const EXECUTOR_TYPES = {
  SHELL: 'shell',
  POWERSHELL: 'powershell',
  CMD: 'cmd',
  BASH: 'bash',
  SH: 'sh',
  PYTHON: 'python'
};

// Agent status
export const AGENT_STATUS = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  IDLE: 'idle',
  BUSY: 'busy',
  ERROR: 'error'
};

// Task status
export const TASK_STATUS = {
  PENDING: 'pending',
  SENT: 'sent',
  RUNNING: 'running',
  COMPLETED: 'completed',
  FAILED: 'failed',
  TIMEOUT: 'timeout',
  CANCELLED: 'cancelled'
};

// Platform types
export const PLATFORMS = {
  WINDOWS: 'windows',
  LINUX: 'linux',
  DARWIN: 'darwin'
};

// Get all agents
const getAgents = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.platform) params.append('platform', filters.platform);
  if (filters.approved !== undefined && filters.approved !== '') params.append('approved', filters.approved);
  if (filters.project) params.append('project', filters.project);
  if (filters.tag) params.append('tag', filters.tag);
  if (filters.group) params.append('group', filters.group);

  const response = await axios.get(`${API_URL}?${params.toString()}`);
  return response.data;
};

// Get connected agents
const getConnectedAgents = async () => {
  const response = await axios.get(`${API_URL}/connected`);
  return response.data;
};

// Get agent statistics
const getAgentStats = async () => {
  const response = await axios.get(`${API_URL}/stats`);
  return response.data;
};

// Get version information
const getVersion = async () => {
  const response = await axios.get('/version');
  return response.data;
};

// Generate API key for new agent
const generateApiKey = async () => {
  const response = await axios.get(`${API_URL}/generate-key`);
  return response.data;
};

// Get single agent
const getAgent = async (id) => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

// Update agent
const updateAgent = async (id, data) => {
  const response = await axios.put(`${API_URL}/${id}`, data);
  return response.data;
};

// Approve agent
const approveAgent = async (id) => {
  const response = await axios.post(`${API_URL}/${id}/approve`);
  return response.data;
};

// Revoke agent approval
const revokeAgent = async (id) => {
  const response = await axios.post(`${API_URL}/${id}/revoke`);
  return response.data;
};

// Delete agent
const deleteAgent = async (id) => {
  const response = await axios.delete(`${API_URL}/${id}`);
  return response.data;
};

// Send task to agent
const sendTask = async (agentId, taskData) => {
  const response = await axios.post(`${API_URL}/${agentId}/task`, taskData);
  return response.data;
};

// Cancel task
const cancelTask = async (agentId, taskId) => {
  const response = await axios.post(`${API_URL}/${agentId}/task/${taskId}/cancel`);
  return response.data;
};

// Get agent tasks
const getAgentTasks = async (agentId, options = {}) => {
  const params = new URLSearchParams();
  if (options.limit) params.append('limit', options.limit);
  if (options.status) params.append('status', options.status);

  const response = await axios.get(`${API_URL}/${agentId}/tasks?${params.toString()}`);
  return response.data;
};

// Get single task
const getTask = async (agentId, taskId) => {
  const response = await axios.get(`${API_URL}/${agentId}/tasks/${taskId}`);
  return response.data;
};

// Send console command to agent
const sendConsoleCommand = async (agentId, command, executorType) => {
  const response = await axios.post(`${API_URL}/${agentId}/console`, { command, executorType });
  return response.data;
};

// Refresh agent system info
const refreshAgent = async (id) => {
  const response = await axios.post(`${API_URL}/${id}/refresh`);
  return response.data;
};

// Send task to multiple agents
const sendBulkTask = async (data) => {
  const response = await axios.post(`${API_URL}/bulk-task`, data);
  return response.data;
};

// Download agent binary (authenticated fetch)
const downloadAgent = async (platform) => {
  const response = await axios.get(`${API_URL}/download/${platform}`, {
    responseType: 'blob'
  });
  const contentDisposition = response.headers['content-disposition'];
  let filename = `purpleteam-agent-${platform}`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?(.+?)"?$/);
    if (match) filename = match[1];
  } else if (platform === 'windows') {
    filename += '.exe';
  }
  const blob = new Blob([response.data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// Helper: Get status color
const getStatusColor = (status) => {
  switch (status) {
    case 'online':
      return 'text-green-500';
    case 'busy':
      return 'text-yellow-500';
    case 'offline':
      return 'text-gray-500';
    case 'error':
      return 'text-red-500';
    default:
      return 'text-gray-500';
  }
};

// Helper: Get task status color
const getTaskStatusColor = (status) => {
  switch (status) {
    case 'completed':
      return 'text-green-500';
    case 'running':
    case 'sent':
      return 'text-blue-500';
    case 'pending':
      return 'text-yellow-500';
    case 'failed':
    case 'timeout':
    case 'cancelled':
      return 'text-red-500';
    default:
      return 'text-gray-500';
  }
};

// Helper: Get platform icon (FontAwesome brands)
const getPlatformIcon = (platform) => {
  switch (platform) {
    case 'windows':
      return 'faWindows';  // Use FontAwesomeIcon with faWindows from brands
    case 'linux':
      return 'faLinux';    // Use FontAwesomeIcon with faLinux from brands
    case 'darwin':
      return 'faApple';    // Use FontAwesomeIcon with faApple from brands
    default:
      return 'faLaptop';   // Use FontAwesomeIcon with faLaptop from solid
  }
};

// Helper: Format bytes
const formatBytes = (bytes) => {
  if (!bytes) return 'N/A';
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper: Format uptime
const formatUptime = (seconds) => {
  if (!seconds) return 'N/A';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.length > 0 ? parts.join(' ') : '< 1m';
};

const agentService = {
  getAgents,
  getConnectedAgents,
  getAgentStats,
  generateApiKey,
  getAgent,
  updateAgent,
  approveAgent,
  revokeAgent,
  deleteAgent,
  sendTask,
  cancelTask,
  getAgentTasks,
  getTask,
  sendConsoleCommand,
  refreshAgent,
  sendBulkTask,
  downloadAgent,
  getVersion,
  getStatusColor,
  getTaskStatusColor,
  getPlatformIcon,
  formatBytes,
  formatUptime,
  EXECUTOR_TYPES,
  AGENT_STATUS,
  TASK_STATUS,
  PLATFORMS
};

export default agentService;
